import {Exception} from "@microsoft/applicationinsights-web";

window.deviceServices = (function() {

    let dotnetHelper;

    const init = async (dotnet) => {
        dotnetHelper = dotnet;

        navigator.mediaDevices.addEventListener('devicechange', async (event) => {
            await dotnetHelper?.invokeMethodAsync('HandleDeviceListChanged');
        });
    }
   
    const disposeStream = (stream) => {
        let tracks = stream.getTracks();

        for(let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            if (track.readyState === 'live') {
                track.stop();
            }
        }
    }

    const getDevices = async () => {
        return await navigator.mediaDevices.enumerateDevices();
    }
    
    const startMonitoringSoundOutput = async function(dotnetObj) {
        if(Howler?.ctx === null){
            throw new Exception("Howler audio context not found.")
        }
        const audioContext = Howler.ctx;
        const analyser = audioContext.createAnalyser();
        const masterGain = Howler.masterGain;
        const scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.6;
        analyser.fftSize = 1024;

        masterGain.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);
        scriptProcessor.onaudioprocess = function() {
            const array = new Uint8Array(analyser.frequencyBinCount);
            // analyser.getByteFrequencyData(array);
            analyser.getByteTimeDomainData(array)
/*            const arraySum = array.reduce((a, value) => a + value, 0);
            const average = Math.round(arraySum / array.length);*/

            // Compute the loudness as the average deviation from the center (128)
            let sum = 0;
            for(let i = 0; i < array.length; i++) {         
                sum += Math.abs(array[i] - 128);       
            }
            const average = sum / array.length;
            const loudness = Math.round(average);
            
            // dotnetObj will return an error if we dispose the object in the client.
            // if this happens we should dispose on the audiocontext on the JS side of things.
            dotnetObj?.invokeMethodAsync('OnSoundLevelReceived', loudness).catch(() => {
                scriptProcessor.onaudioprocess = function() {}
                scriptProcessor.disconnect();
                analyser.disconnect();
            });
        };
    }
    const startMonitoringMicrophone = async function(dotnetObj, microphoneDeviceId) {
        const constraints = {
            audio: {deviceId: microphoneDeviceId ? {exact: microphoneDeviceId} : undefined}
        };
        
        let stream = await navigator.mediaDevices.getUserMedia(constraints);
        let audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        const scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.6;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);
        scriptProcessor.onaudioprocess = function() {
            const array = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(array);
            const arraySum = array.reduce((a, value) => a + value, 0);
            const average = Math.round(arraySum / array.length);
            
            // dotnetObj will return an error if we dispose the object in the client.
            // if this happens we should dispose on the audiocontext on the JS side of things.
            dotnetObj?.invokeMethodAsync('OnSoundLevelReceived', average).catch(() => {
                if (audioContext == null) return;
                audioContext.close();
                audioContext = null;

                scriptProcessor.disconnect();
                analyser.disconnect();
                microphone.disconnect();
                
                disposeStream(stream);
            });
        };
    }
    
    const requestMicrophoneAccess = async(deviceId) => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMicrophoneAccess = deviceId
            ? devices.some(device => device.deviceId === deviceId && device.kind === 'audioinput' && device.label)
            : devices.some(device => device.kind === 'audioinput' && device.label);

        if (hasMicrophoneAccess) return "Allowed";
        return await requestUserMedia({audio: {deviceId:deviceId}});
    }
    
    const requestCameraAccess = async(deviceId) => {
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCameraAccess = deviceId
            ? devices.some(device => device.deviceId === deviceId && device.kind === 'videoinput' && device.label)
            : devices.some(device => device.kind === 'videoinput' && device.label);
        
        if (hasCameraAccess) return "Allowed";
        return await requestUserMedia({video: {deviceId:deviceId}});
    }
    
    const requestBothCameraAndMicrophone = async() => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCameraAccess = devices.some(device => device.kind === 'videoinput' && device.label);
        const hasMicrophoneAccess = devices.some(device => device.kind === 'audioinput' && device.label);

        if (hasCameraAccess && hasMicrophoneAccess) {
            return "Allowed";
        } else {
            return await requestUserMedia({video: {deviceId:undefined}, audio: {deviceId:undefined}});
        }
    }

    const requestUserMedia = async(constraints) => {
        try {
            let stream = await navigator.mediaDevices.getUserMedia(constraints);
            disposeStream(stream);
        } catch (err) {
            if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError" || err.name === "OverconstrainedError" || err.name === "ConstraintNotSatisfiedError") {
                return "DeviceNotFound";
            } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
                return "AlreadyInUse";
            } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
                return "PermissionDenied";
            } else {
                return "UnknownError";
            }
        }

        return "Allowed";
    }
    
    const requestDevicePermissions = async (microphoneRequired, cameraRequired) => {
        let speakerAccess;
        let microphoneAccess;
        let cameraAccess;

        if (cameraRequired && microphoneRequired) {
            let cameraAndMicrophoneResult = await requestBothCameraAndMicrophone();
            if (cameraAndMicrophoneResult === "Allowed") {
                cameraAccess = "Allowed";
                microphoneAccess = "Allowed"
            } else {
                cameraAccess = await requestCameraAccess();
                microphoneAccess = await requestMicrophoneAccess();
            }
        } else if (microphoneRequired) {
            microphoneAccess = await requestMicrophoneAccess();
        }

        if(!('setSinkId' in HTMLAudioElement.prototype) || !('setSinkId' in HTMLVideoElement.prototype) || !('setSinkId' in AudioContext.prototype)) {
            speakerAccess = "BrowserNotSupported";
        } else if (microphoneAccess === "PermissionDenied" || microphoneAccess === "Allowed") {
            speakerAccess = microphoneAccess;
        } else {
            speakerAccess = "RequiresOtherPermission";
        }
        
        return {
            SpeakerAccess: speakerAccess,
            MicrophoneAccess: microphoneAccess,
            CameraAccess: cameraAccess
        }
    }

    return {
        init: init,
        getDevices: getDevices,
        startMonitoringMicrophone: startMonitoringMicrophone,
        requestDevicePermissions: requestDevicePermissions,
        requestCameraAccess: requestCameraAccess,
        requestMicrophoneAccess: requestMicrophoneAccess,
        startMonitoringSoundOutput: startMonitoringSoundOutput
    }
})();